/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import React, { useMemo } from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import helpCenter from 'js/lib/courseraHelpcenter';

import type { Theme } from '@coursera/cds-core';
import { Button, breakpoints, useTheme } from '@coursera/cds-core';

import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/my-purchases-graphql';

const helpCenterUrl = helpCenter.getRequestRefundHelpLink(true);
type Props = {
  handleClose: () => void;
  hasEarnedCertificate: boolean;
};
const useStyles = (theme: Theme) => ({
  rcRefundErrorModal: css({
    'div.rc-Modal': {
      'div.c-modal-content': {
        padding: '48px 52px',
        textAlign: 'left',

        [breakpoints.down('sm')]: {
          padding: '48px 36px',
        },
      },
    },
  }),
  modalTitle: css({
    marginBottom: '12px',
  }),
  buttonContainer: css({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '32px',
  }),
});
const RefundErrorModal = ({ hasEarnedCertificate, handleClose }: Props) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const modalMessage = useMemo(() => {
    if (hasEarnedCertificate) {
      return _t(
        `Congratulations, you have earned the certificate
for this product. You are no longer eligible for a refund.`
      );
    } else {
      return (
        <FormattedHTMLMessage
          message={_t(
            `We currently cannot process this request. If you are requesting a refund,
please contact our support team by following the steps below: Go to the
<a href="{url}">Learner Help Center: Request a Refund</a>.
Scroll down to the bottom of the article and click
“Contact us” Select the option to submit a request for
a refund.`
          )}
          url={helpCenterUrl}
        />
      );
    }
  }, [hasEarnedCertificate]);
  return (
    <div css={styles.rcRefundErrorModal}>
      <Modal modalName="refund_error_modal" handleClose={handleClose}>
        <div className="title modal-container">
          <h3 className="modal-title headline-3-text" css={styles.modalTitle}>
            {_t("We're sorry")}
          </h3>
        </div>
        <div className="content modal-container">
          <p>{modalMessage}</p>
          <div css={styles.buttonContainer}>
            <Button variant="primary" data-test="close_button" onClick={handleClose}>
              {_t('OK')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RefundErrorModal;
